let isScrollingToSection = false;
let scrollTimer = null;
let intersectionTimer = null;

const sections = document.querySelectorAll(
  '.post-content [data-table-of-contents="true"]'
);

const intersectionData = {};
const sideTocWrapper = document.querySelector('.post-side-toc-wrapper');

// When copying a section in Kentico, it duplicates the instance GUID
// and breaks the links. We keep track of seen section IDs to avoid this.
// When a duplicate section ID is found, we update the ID and links.
const seenSectionIds = new Set();
let duplicateSectionCount = 0;

sections.forEach(section => {
  if (!section.id) return;

  if (seenSectionIds.has(section.id)) {
    const prevId = section.id;

    duplicateSectionCount++;
    section.id = `${prevId}-${duplicateSectionCount}`;

    // We know that the links we need to update are the second
    // items when querying based on the section ID
    const topTocLink = document.querySelectorAll(
      `.post-toc-wrapper a[href="#${prevId}"]`
    )[1];

    const sideTocLink = document.querySelectorAll(
      `.post-side-toc-wrapper a[href="#${prevId}"]`
    )[1];

    const sideTocProgressBar = document.querySelectorAll(
      `.post-side-toc-wrapper .post-toc-progress-item[data-target="${prevId}"]`
    )[1];

    const href = `#${section.id}`;

    if (topTocLink) topTocLink.href = href;
    if (sideTocLink) sideTocLink.href = href;
    if (sideTocProgressBar) sideTocProgressBar.dataset.target = section.id;
  }

  seenSectionIds.add(section.id);
});

let didTapProgressBar = false;

// Smooth scroll to section, accounting for sticky header
if (sections.length) {
  document.querySelectorAll('.post-toc-items a').forEach(link => {
    link.addEventListener('click', e => {
      if (!link.hash || didTapProgressBar) return;

      const section = document.querySelector(link.hash);
      if (!section) return;

      e.preventDefault();

      // When clicking a TOC link while the side overlay is open,
      // ensure only that link is active during the scroll animation
      if (sideTocWrapper) {
        isScrollingToSection = true;

        clearTimeout(scrollTimer);
        clearTimeout(intersectionTimer);

        setActiveSection(section.id);
      }

      const headerHeight = document.querySelector('#header')?.offsetHeight ?? 0;

      const sectionPadding = window
        .getComputedStyle(section)
        .getPropertyValue('--section-spacing');

      const offset = window.scrollY - headerHeight - (parseInt(sectionPadding) || 0) + 3;

      window.scrollTo({
        top: section.getBoundingClientRect().top + offset,
        behavior: 'smooth'
      });
    });
  });
}

// Update side table of contents with current section
function initSideToc() {
  if (!window.IntersectionObserver || !sideTocWrapper || !sections.length) {
    return;
  }

  // Prevent automatic scrolling to section when tapping progress bar on tablets
  const progressWrapper = document.querySelector('.post-toc-progress');

  if (progressWrapper) {
    progressWrapper.addEventListener('touchstart', () => (didTapProgressBar = true), {
      useCapture: true
    });

    document.addEventListener('touchend', () => {
      setTimeout(() => (didTapProgressBar = false), 50);
    });
  }

  // Hide side TOC when scrolled above or below article
  // The rootMargins can be overridden by setting global JS variables
  const topSentinel = document.querySelector('.toc-sentinel-top');
  const bottomSentinel = document.querySelector('.toc-sentinel-bottom');

  if (topSentinel) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const hasStarted = entry.isIntersecting || entry.boundingClientRect.top <= 0;
        sideTocWrapper.classList.toggle('has-started', hasStarted);
      },
      {
        rootMargin: window.tocTopSentinelObserverRootMargin || '0px 0px -50% 0px'
      }
    );

    observer.observe(topSentinel);
  }

  if (bottomSentinel) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const hasFinished = entry.isIntersecting || entry.boundingClientRect.top <= 0;
        sideTocWrapper.classList.toggle('has-finished', hasFinished);
      },
      {
        rootMargin: window.tocBottomSentinelObserverRootMargin || '0px 0px -30% 0px'
      }
    );

    observer.observe(bottomSentinel);
  }

  sideTocWrapper.classList.add('initialized');

  // Set current section while the user scrolls through the article.
  // We highlight the progress bar for that section, and add an
  // active class to the link in the overlay table of contents.
  const enableIntersectionChecks = () => {
    if (!isScrollingToSection) return;

    clearTimeout(scrollTimer);
    isScrollingToSection = false;
    handleIntersection();
  };

  window.addEventListener('scroll', () => {
    if (!isScrollingToSection) return;

    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(enableIntersectionChecks, 100);
  });

  window.addEventListener('wheel', enableIntersectionChecks);
  window.addEventListener('touchmove', enableIntersectionChecks);

  const handleIntersection = () => {
    clearTimeout(intersectionTimer);

    if (isScrollingToSection) return;

    intersectionTimer = setTimeout(() => {
      if (isScrollingToSection) return;
      let currentSectionId = null;

      Object.keys(intersectionData).forEach(id => {
        const { ratio, index } = intersectionData[id];

        if (ratio <= 0) return;
        if (currentSectionId === null) {
          currentSectionId = id;
        } else {
          const currentSection = intersectionData[currentSectionId];

          if (
            ratio > currentSection.ratio ||
            (ratio === currentSection.ratio && index < currentSection.index)
          ) {
            currentSectionId = id;
          }
        }
      });

      setActiveSection(currentSectionId);
    }, 5);
  };

  const headerHeight = document.querySelector('#header')?.offsetHeight ?? 0;

  const sectionObserver = new IntersectionObserver(
    entries => {
      let entry = null;

      entries.forEach(item => {
        if (!entry || item.intersectionRatio > entry.intersectionRatio) {
          entry = item;
        }
      });

      if (!entry) return;

      const { target, intersectionRatio } = entry;

      if (intersectionData[entry.target.id]) {
        intersectionData[target.id].ratio = Math.round(intersectionRatio * 10);

        intersectionData[target.id].entry = entry;
      }

      handleIntersection();
    },
    {
      rootMargin: window.tocSectionObserverRootMargin || `${headerHeight}px 0px 0px 0px`,
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
    }
  );

  sections.forEach((section, index) => {
    if (!section.id) return;

    const link = sideTocWrapper.querySelector(`.post-toc-items a[href="#${section.id}"]`);
    const progressBar = sideTocWrapper.querySelector(
      `.post-toc-progress-item[data-target="${section.id}"]`
    );

    intersectionData[section.id] = { section, link, progressBar, index, ratio: 0 };
    sectionObserver.observe(section);
  });
}

function setActiveSection(currentSectionId = null) {
  Object.keys(intersectionData).forEach(id => {
    const hasCurrentSection = currentSectionId !== null;
    const isActive = hasCurrentSection && currentSectionId === id;

    const { progressBar, link } = intersectionData[id];

    progressBar?.classList.toggle('active', isActive);
    link?.classList.toggle('active', isActive);
  });
}

initSideToc();
